/**
 * Pure WHEP/ICE primitives used by `useWhepPlayer`. Kept free of React state
 * so the gathering-timeout logic can be unit-tested in isolation (#379).
 */

/**
 * Extract a human-readable failure reason from a WHEP error response body.
 * MediaMTX answers with a short plain-text reason (e.g. "codecs not supported
 * by client"); tolerate a JSON `{error}` envelope too. Returns null when the
 * body carries nothing usable, so callers can fall back to a generic message.
 */
export function extractWhepReason(body: string): string | null {
  const text = body.trim();
  if (!text) return null;
  try {
    const parsed: unknown = JSON.parse(text);
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      typeof (parsed as Record<string, unknown>).error === "string"
    ) {
      return (parsed as Record<string, string>).error;
    }
  } catch {
    // Plain-text body — fall through to the first line.
  }
  const firstLine = text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)[0];
  return firstLine ?? null;
}

/**
 * Resolve when ICE gathering completes, or reject with:
 * - `AbortError` when `signal` aborts (attempt teardown / unmount),
 * - `TimeoutError` when gathering does not complete within `timeoutMs`.
 *
 * The `TimeoutError` rejection is what keeps `useWhepPlayer` out of the
 * "stuck on connecting forever" failure mode (#379): the hook's catch treats
 * it like any other failed attempt and retries with backoff.
 */
export function waitForIceGatheringComplete(
  pc: RTCPeerConnection,
  signal: AbortSignal,
  timeoutMs: number,
): Promise<void> {
  if (pc.iceGatheringState === "complete") {
    return Promise.resolve();
  }
  if (signal.aborted) {
    return Promise.reject(
      new DOMException("Negotiation aborted", "AbortError"),
    );
  }

  return new Promise((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (timer !== null) clearTimeout(timer);
      pc.removeEventListener("icegatheringstatechange", onStateChange);
      signal.removeEventListener("abort", onAbort);
    };

    const onStateChange = () => {
      if (pc.iceGatheringState !== "complete") return;
      cleanup();
      resolve();
    };

    const onAbort = () => {
      cleanup();
      reject(new DOMException("Negotiation aborted", "AbortError"));
    };

    const onTimeout = () => {
      timer = null;
      cleanup();
      reject(
        new DOMException(
          `ICE gathering did not complete within ${timeoutMs}ms`,
          "TimeoutError",
        ),
      );
    };

    pc.addEventListener("icegatheringstatechange", onStateChange);
    signal.addEventListener("abort", onAbort, { once: true });
    timer = setTimeout(onTimeout, timeoutMs);
  });
}
